import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './StudentLogin.css';
import { supabase } from '../../lib/supabaseClient';
import { HiEye, HiEyeOff } from 'react-icons/hi';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { useAuth } from '../../lib/AuthProvider';

const StudentLogin: React.FC = () => {
  const navigate = useNavigate();
  const { user, userType, loading: authLoading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Check for existing session on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          console.log('Found existing session, checking profile...');
        }
      } catch (error) {
        console.error('Error checking session:', error);
      }
    };
    
    checkExistingSession();
  }, []);

  // Monitor auth state changes
  useEffect(() => {
    console.log('Auth state check:', { 
      hasUser: !!user,
      userType,
      authLoading,
      loading,
      timestamp: new Date().toISOString()
    });

    // Handle successful student login
    if (!authLoading && user && userType === 'student') {
      console.log('Student login confirmed, redirecting to home...');
      setError('');
      setLoading(false);
      navigate('/student/home', { replace: true });
      return;
    }

    // Handle non-student users
    if (!authLoading && user && userType && userType !== 'student') {
      console.log('Non-student user detected, signing out...');
      setError('This login is for students only');
      setLoading(false);
      supabase.auth.signOut();
      return;
    }

    // Handle case where user exists but no userType (profile still loading)
    if (!authLoading && user && !userType) {
      console.log('User authenticated but profile still loading...');
      // Keep loading state active, don't redirect yet
      return;
    }

    // Handle case where auth is complete but no user (login failed or user signed out)
    if (!authLoading && !user && loading) {
      console.log('Auth complete, no user found, resetting loading state');
      setLoading(false);
      return;
    }

  }, [user, userType, authLoading, loading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Add a timeout to prevent infinite loading
    const loginTimeout = setTimeout(() => {
      console.log('Login process timed out, resetting state...');
      setLoading(false);
      setError('Login timed out. Please try again.');
    }, 15000); // 15 second timeout

    try {
      console.log('Starting login process...');
      
      // Simple sign out if there's an existing session, but don't clear all storage
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (currentSession) {
        console.log('Found existing session, signing out...');
        await supabase.auth.signOut();
        // Wait a moment for sign out to complete
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      console.log('Attempting sign in for:', email);
      
      // Sign in with new credentials
      const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (signInError) {
        throw signInError;
      }

      if (!signInData.session || !signInData.user) {
        throw new Error('No session or user data returned from login');
      }

      console.log('Sign in successful:', {
        userId: signInData.user.id,
        hasSession: !!signInData.session,
        event: 'SIGNED_IN',
        timestamp: new Date().toISOString()
      });

      // Clear the timeout since login was successful
      clearTimeout(loginTimeout);
      
      // Let the AuthProvider handle the rest - don't interfere with the normal flow
      console.log('Login complete, AuthProvider will handle profile loading and navigation');
      
    } catch (err) {
      console.error('Login failed:', err);
      clearTimeout(loginTimeout);
      
      let errorMessage = 'An unexpected error occurred';
      
      if (err instanceof Error) {
        if (err.message.includes('Invalid login credentials')) {
          errorMessage = 'Invalid email or password';
        } else if (err.message.includes('Email not confirmed')) {
          errorMessage = 'Please verify your email address first';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  const goToSupervisorLogin = () => {
    navigate('/supervisor');
  };

  const goToRegistration = () => {
    navigate('/register');
  };

  const goToForgotPassword = () => {
    navigate('/forgot-password');
  };

  

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="logo">
          <h1>InternTr<FaMapMarkerAlt className="location-icon" />ckr</h1>
          <p className="tagline">Anytime. Anywhere.</p>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <label>Email</label>
            <div className="input-with-clear">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>
            <div className="help-text">Please enter your email</div>
          </div>
          
          <div className="input-group">
            <label>Password</label>
            <div className="input-with-clear">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <HiEyeOff size={18} /> : <HiEye size={18} />}
              </button>
            </div>
            <div className="help-text">Please enter your password</div>
          </div>
          
          {error && <div className="error-message">{error}</div>}
          <div className="actions-container">
            <div className="button-row">
              <button type="submit" className="action-button login-button" disabled={loading}>
                {loading ? 'Logging in...' : 'Login'}
              </button>
              <button type="button" className="action-button register-button" onClick={goToRegistration} disabled={loading}>
                Register
              </button>
            </div>
          </div>
          <div className="forgot-password">
            <p>Forgot password? <a href="#" onClick={goToForgotPassword} className="link-button">Click here</a>.</p>
          </div>
        </form>
        <div className="supervisor-link">
          <p>Not a Student? <a href="#" onClick={goToSupervisorLogin} className="link-button">Click here to sign in as a OJT Coordinator / Supervisor</a>.</p>
        </div>
      </div>
    </div>
  );
};

export default StudentLogin;
